<!-- eslint-disable solvari-vue/bem-template-class -- generic filename: no component name to derive the BEM block from -->
<template>
  <NuxtLayout name="layout-default">
    <div class="page-error">
      <SLoader
        v-if="resolvingRedirect"
        class="page-error__loader"
        size="3rem"
      />

      <SAppContainer v-else class="page-error__content">
        <figure class="page-error_image">
          <img
            alt=""
            :src="
              useCloudinary(
                'v1749650017/production/resources/apps/mainsite/rocket.svg',
                { width: 120, height: 120 },
              )
            "
          />
        </figure>
        <article class="page-error_content">
          <h1 v-if="error && errorTitle">{{ errorTitle }}: {{ error.url }}</h1>
          <ul>
            <li>
              <i class="fas fa-check-circle" />
              <p>{{ tr("main_site.error.message_1") }}</p>
            </li>
            <li>
              <i class="fas fa-check-circle" />
              <p v-html="tr('main_site.error.message_2')" />
            </li>
            <li>
              <i class="fas fa-check-circle" />
              <p>{{ tr("main_site.error.message_3") }}</p>
            </li>
          </ul>
          <SButton @click="handleError">
            {{ tr("main_site.error.back") }}
          </SButton>
        </article>
      </SAppContainer>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import type { PropType } from "vue";

import { clearError, navigateTo, useRequestURL } from "#app";
import { computed, onMounted, ref } from "vue";

import { SButton } from "@solvari/common-fe";
import { useCloudinary } from "@solvari/common-fe/integrations";
import { SAppContainer, SLoader } from "@solvari/components";
import { useI18nMainSiteLayout } from "@solvari/translations";
import { isError } from "@solvari/utils";

import { ziggy } from "@/shared/libs/ziggy";
import { getRedirects } from "~/shared/api/redirects.api.ts";
import { useHostLocale } from "~/shared/composables/useHost.ts";
import {
  useBaseMeta,
  useSeoNormalized,
} from "~/shared/composables/useMetaData.ts";
import { resolveRedirect } from "~/shared/libs/redirectMatching.ts";

const { tr } = useI18nMainSiteLayout();

const props = defineProps({
  error: {
    type: Object as PropType<{
      data?: { clientSideNavigation?: boolean };
      statusCode?: number;
      url?: string;
    }>,
    default: undefined,
  },
});

const resolvingRedirect = ref(
  props.error?.data?.clientSideNavigation === true &&
    props.error?.statusCode === 404,
);

onMounted(async () => {
  if (!resolvingRedirect.value) {
    return;
  }

  const redirects = await getRedirects(useHostLocale());
  const resolved = isError(redirects)
    ? null
    : resolveRedirect(redirects, useRequestURL());

  if (!resolved) {
    resolvingRedirect.value = false;
    return;
  }

  await navigateTo(resolved.to, {
    external: true,
    redirectCode: resolved.status,
    replace: true,
  });
});

const handleError = () =>
  clearError({ redirect: ziggy("mainSite.companyWall", { page: undefined }) });

const errorTitle = computed(() => {
  if (!props.error) {
    return "";
  }

  const statusCode = String(props.error.statusCode);

  if (statusCode.startsWith("4")) {
    return tr("main_site.error.not_found");
  }

  if (statusCode.startsWith("5")) {
    return tr("main_site.error.internal_server_error");
  }

  return tr("main_site.error.unknown");
});

useBaseMeta();
useSeoNormalized({
  title: errorTitle,
  index: false,
});
</script>

<style lang="postcss">
/* stylelint-disable solvari/bem-block -- generic filename: no component name to derive the BEM block from */
.page-error {
  @apply bg-primary-50;

  &__loader {
    @apply flex min-h-80 items-center justify-center text-primary-700;
  }

  &__content {
    @apply grid min-h-80 items-center justify-center p-6 gap-6;
    grid-template-columns: 1fr;

    @screen 800 {
      grid-template-columns: 10rem auto;
    }
  }

  @screen 800 {
    &_content {
      h1 {
        @apply text-3xl;
      }
    }
  }

  &_image {
    @apply p-4;

    img {
      @apply mx-auto w-full max-w-28;
    }
  }

  &_content {
    h1 {
      @apply mb-4 text-2xl text-primary-700;
    }

    ul {
      @apply mb-4 flex flex-col gap-4;
    }

    li {
      @apply flex items-center;
    }

    i {
      @apply mr-3 p-2 text-accent-300;
    }
  }
}
</style>
